import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { FolderKanban, Users, FileBarChart2, Target, FolderGit2, Gauge, Trophy } from 'lucide-react';
import { ProjectReportsDemo } from '@/components/project-manager/ProjectReportsDemo';
import GoalsStrategiesDialog from '@/components/project-manager/GoalsStrategiesDialog';
import ProjectsTasksDialog from '@/components/project-manager/ProjectsTasksDialog';
import GrowthIntelligenceDialog from '@/components/project-manager/GrowthIntelligenceDialog';
import MilestonesRewardsDialog from '@/components/project-manager/MilestonesRewardsDialog';
import {
  INITIAL_GOALS, INITIAL_STRATEGIES, INITIAL_PROJECTS, INITIAL_TASKS, INITIAL_MILESTONES,
} from '@/components/project-manager/ProjectPlanningDemo';
import TeamMembers from '@/pages/new-app/TeamMembers';

function DashboardTile({ icon: Icon, title, summary, onClick, accent }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left rounded-xl border border-white/10 bg-white/[0.02] p-4 transition-colors hover:bg-white/5 hover:border-blue-400/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3A7BFF]/60"
      data-testid={`pm-tile-${title.replace(/\s+/g, '-').toLowerCase()}`}
    >
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${accent}`}>
        <Icon className="h-4.5 w-4.5" />
      </div>
      <p className="text-sm font-semibold text-white">{title}</p>
      <p className="text-xs text-[#8A93A6] mt-1">{summary}</p>
    </button>
  );
}

/**
 * Project Manager — consolidates Project Management (compact overview +
 * Goals & Strategies / Projects & Tasks / Growth Intelligence / Milestones
 * & Rewards tiles), reporting, and Team Management under ACCOUNT.
 *
 * Goals/strategies/projects/tasks/milestones state lives here (lifted out
 * of the former single stacked ProjectPlanningDemo component) so the Goals
 * & Strategies, Projects & Tasks, and Milestones & Rewards dialogs all stay
 * in sync — Level 1 (visual) + Level 2 (simulated interaction) only.
 *
 * `?tab=team` deep-links straight to Team Management, preserving old links
 * that used to point at /app/account/team directly.
 * `?report=weekly` opens the report panel with Weekly Performance selected,
 * preserving the old /app/account/weekly-digest link.
 */
export default function ProjectManager() {
  const [searchParams, setSearchParams] = useSearchParams();
  const reportParam = searchParams.get('report');
  const initialTab = searchParams.get('tab') === 'team' && !reportParam ? 'team' : 'project';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [reportsOpen, setReportsOpen] = useState(!!reportParam);

  // Lifted Goals/Strategies/Projects/Tasks/Milestones state — shared across
  // the Goals & Strategies, Projects & Tasks, and Milestones & Rewards tiles.
  const [goals, setGoals] = useState(INITIAL_GOALS);
  const [strategies, setStrategies] = useState(INITIAL_STRATEGIES);
  const [projects, setProjects] = useState(INITIAL_PROJECTS);
  const [tasks, setTasks] = useState(INITIAL_TASKS);
  const [milestones, setMilestones] = useState(INITIAL_MILESTONES);

  const [openTile, setOpenTile] = useState(null); // 'goals' | 'projects' | 'growth' | 'milestones' | null

  useEffect(() => {
    if (reportParam) {
      setActiveTab('project');
      setReportsOpen(true);
    }
  }, [reportParam]);

  // Clear ?report=weekly once the dialog is closed, so refreshing the page
  // (or reopening Generate Report later) doesn't keep forcing Weekly
  // Performance back as the selected report type.
  const handleReportsOpenChange = (next) => {
    setReportsOpen(next);
    if (!next && reportParam) {
      searchParams.delete('report');
      setSearchParams(searchParams, { replace: true });
    }
  };

  // ── Dashboard summary (sample, clearly labeled) — same calculations as before ──
  const activeGoalsCount = goals.length;
  const activeProjectsCount = projects.filter((p) => p.stage !== 'Published').length;
  const tasksDueCount = tasks.filter((t) => t.status !== 'Done').length;
  const avgMilestonePct = milestones.length
    ? Math.round(milestones.reduce((sum, m) => sum + (m.target ? Math.min(100, (m.current / m.target) * 100) : 0), 0) / milestones.length)
    : 0;

  return (
    <div className="container mx-auto p-6 max-w-6xl" data-testid="project-manager-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white">Project Manager</h1>
        <p className="text-[#8A93A6] mt-1 max-w-2xl">
          Manage your creator business, goals, strategies, projects, and team.
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="glass mb-6">
          <TabsTrigger value="project" className="flex items-center gap-2" data-testid="project-manager-tab-project">
            <FolderKanban className="h-4 w-4" />
            Project Management
          </TabsTrigger>
          <TabsTrigger value="team" className="flex items-center gap-2" data-testid="project-manager-tab-team">
            <Users className="h-4 w-4" />
            Team Management
          </TabsTrigger>
        </TabsList>

        <TabsContent value="project" data-testid="project-manager-project-content">
          <div className="flex justify-end mb-3">
            <Button size="sm" variant="outline" className="border-white/10" onClick={() => setReportsOpen(true)} data-testid="generate-report-btn">
              <FileBarChart2 className="h-3.5 w-3.5 mr-1.5" />
              Generate Report
            </Button>
          </div>

          {/* Summary metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="project-dashboard-summary">
            <Card className="glass border-white/5">
              <CardContent className="p-3.5">
                <p className="text-[10px] uppercase tracking-wider text-[#8A93A6]">Active Goals</p>
                <p className="text-2xl font-bold text-white tabular-nums mt-1" data-testid="kpi-active-goals">{activeGoalsCount}</p>
                <p className="text-[10px] text-[#555E6E] mt-0.5">Sample data</p>
              </CardContent>
            </Card>
            <Card className="glass border-white/5">
              <CardContent className="p-3.5">
                <p className="text-[10px] uppercase tracking-wider text-[#8A93A6]">Active Projects</p>
                <p className="text-2xl font-bold text-white tabular-nums mt-1" data-testid="kpi-active-projects">{activeProjectsCount}</p>
                <p className="text-[10px] text-[#555E6E] mt-0.5">Sample data</p>
              </CardContent>
            </Card>
            <Card className="glass border-white/5">
              <CardContent className="p-3.5">
                <p className="text-[10px] uppercase tracking-wider text-[#8A93A6]">Tasks Due</p>
                <p className="text-2xl font-bold text-white tabular-nums mt-1" data-testid="kpi-tasks-due">{tasksDueCount}</p>
                <p className="text-[10px] text-[#555E6E] mt-0.5">Sample data</p>
              </CardContent>
            </Card>
            <Card className="glass border-white/5">
              <CardContent className="p-3.5">
                <p className="text-[10px] uppercase tracking-wider text-[#8A93A6]">Milestones Progress</p>
                <p className="text-2xl font-bold text-white tabular-nums mt-1" data-testid="kpi-milestones-progress">{avgMilestonePct}%</p>
                <p className="text-[10px] text-[#555E6E] mt-0.5">Sample data</p>
              </CardContent>
            </Card>
          </div>

          {/* Four Project Management tiles */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="project-manager-tiles">
            <DashboardTile
              icon={Target} title="Goals & Strategies" accent="bg-cyan-500/10 text-cyan-400"
              summary={`${goals.length} goal${goals.length === 1 ? '' : 's'} · ${strategies.length} strateg${strategies.length === 1 ? 'y' : 'ies'}`}
              onClick={() => setOpenTile('goals')}
            />
            <DashboardTile
              icon={FolderGit2} title="Projects & Tasks" accent="bg-green-500/10 text-green-400"
              summary={`${activeProjectsCount} active project${activeProjectsCount === 1 ? '' : 's'} · ${tasksDueCount} task${tasksDueCount === 1 ? '' : 's'} due`}
              onClick={() => setOpenTile('projects')}
            />
            <DashboardTile
              icon={Gauge} title="Growth Intelligence" accent="bg-violet-500/10 text-violet-400"
              summary="Snapshot, priorities, tasks & coaching"
              onClick={() => setOpenTile('growth')}
            />
            <DashboardTile
              icon={Trophy} title="Milestones & Rewards" accent="bg-amber-500/10 text-amber-400"
              summary={`${avgMilestonePct}% avg milestone progress · streaks & badges`}
              onClick={() => setOpenTile('milestones')}
            />
          </div>
        </TabsContent>

        <TabsContent value="team" data-testid="project-manager-team-content">
          <TeamMembers embedded />
        </TabsContent>
      </Tabs>

      <ProjectReportsDemo
        open={reportsOpen}
        onOpenChange={handleReportsOpenChange}
        defaultReportType={reportParam === 'weekly' ? 'weekly_performance' : undefined}
      />

      <GoalsStrategiesDialog
        open={openTile === 'goals'} onOpenChange={(v) => setOpenTile(v ? 'goals' : null)}
        goals={goals} setGoals={setGoals}
        strategies={strategies} setStrategies={setStrategies}
        milestones={milestones} setMilestones={setMilestones}
      />
      <ProjectsTasksDialog
        open={openTile === 'projects'} onOpenChange={(v) => setOpenTile(v ? 'projects' : null)}
        goals={goals} strategies={strategies}
        projects={projects} setProjects={setProjects}
        tasks={tasks} setTasks={setTasks}
      />
      <GrowthIntelligenceDialog
        open={openTile === 'growth'} onOpenChange={(v) => setOpenTile(v ? 'growth' : null)}
      />
      <MilestonesRewardsDialog
        open={openTile === 'milestones'} onOpenChange={(v) => setOpenTile(v ? 'milestones' : null)}
        goals={goals} milestones={milestones}
        onManageMilestones={() => setOpenTile('goals')}
      />
    </div>
  );
}
